import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChatUnread } from '../contexts/ChatUnreadContext';
import { chatAPI } from '../utils/api';
import { firestoreDb } from '../utils/firebase';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import { Send, ArrowLeft, MessageSquare, Search, X, CornerUpLeft } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import LinkPreviewCard from '../components/LinkPreviewCard';
import ChatMessageBubble from '../components/chat/ChatMessageBubble';
import ChatActionDrawer from '../components/chat/ChatActionDrawer';
import MessageText from '../components/chat/MessageText';
import { useMentionAutocomplete } from '../hooks/useMentionAutocomplete';

const PAGE_SIZE = 30;

const ChatPage = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user, firebaseReady } = useAuth();
  const { setActiveChat } = useChatUnread();
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [totalMessages, setTotalMessages] = useState(0);
  // Reaction state
  const [pickerMsgId, setPickerMsgId] = useState(null);
  // Reply state
  const [replyTo, setReplyTo] = useState(null);
  // Mobile long-press drawer
  const [longPressMsg, setLongPressMsg] = useState(null);

  // Close reaction picker on any outside click
  useEffect(() => {
    if (!pickerMsgId) return;
    const close = () => setPickerMsgId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [pickerMsgId]);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const mentionListRef = useRef(null);
  const initialScrollDone = useRef(false);
  // Track message IDs already loaded via REST to avoid Firestore duplicates
  const loadedIdsRef = useRef(new Set());
  const pendingMsgIdRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressStartPosRef = useRef(null);
  // Track active chat for use inside Firestore callbacks without re-creating listeners
  const activeChatIdRef = useRef(chatId);
  // Timestamp when page loaded — used to skip historical messages in global listener
  const listenFromRef = useRef(new Date().toISOString());
  // Typing indicator
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const typingTimerRef = useRef(null);
  const typingSentRef = useRef(false);
  // Gate to ensure chat list is only fetched once (not on every chatId navigation)
  const chatListLoadedRef = useRef(false);

  // Participants for @mention autocomplete — just the other person in 1-on-1 (no @everyone)
  const chatParticipants = useMemo(() => {
    const chat = chats.find(c => c.chat_id === chatId);
    if (!chat?.other_user?.name) return [];
    return [{ member_id: chat.other_user.user_id, name: chat.other_user.name }];
  }, [chatId, chats]);

  const {
    mentionOpen,
    setMentionOpen,
    mentionIdx,
    filteredParticipants,
    selectMention,
    onInputChange,
    onKeyDown: handleKeyDown,
    onInputBlur: handleInputBlur,
  } = useMentionAutocomplete({ text, setText, participants: chatParticipants, inputRef });

  const loadChats = useCallback(async () => {
    try {
      const res = await chatAPI.getChats();
      setChats(res.data);
    } catch { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async () => {
    if (!chatId) return;
    try {
      const res = await chatAPI.getMessages(chatId, 0, PAGE_SIZE);
      const { items, total, has_more } = res.data;
      setMessages(items);
      setTotalMessages(total);
      setHasOlder(has_more);
      loadedIdsRef.current = new Set(items.map(m => m.message_id));
      initialScrollDone.current = false;
    } catch { /* ignore */ }
  }, [chatId]);

  const loadOlderMessages = useCallback(async () => {
    if (!chatId || loadingOlder || !hasOlder) return;
    setLoadingOlder(true);
    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight || 0;
    try {
      const skip = totalMessages - messages.length - PAGE_SIZE;
      const actualSkip = Math.max(0, skip);
      const actualLimit = skip < 0 ? PAGE_SIZE + skip : PAGE_SIZE;
      if (actualLimit <= 0) { setHasOlder(false); setLoadingOlder(false); return; }
      const res = await chatAPI.getMessages(chatId, messages.length, PAGE_SIZE);
      const { items, has_more } = res.data;
      if (items.length > 0) {
        items.forEach(m => loadedIdsRef.current.add(m.message_id));
        setMessages(prev => [...items, ...prev]);
        setHasOlder(has_more);
        requestAnimationFrame(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - prevScrollHeight;
          }
        });
      } else {
        setHasOlder(false);
      }
    } catch { /* ignore */ }
    finally { setLoadingOlder(false); }
  }, [chatId, loadingOlder, hasOlder, messages.length, totalMessages]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      if (!chatListLoadedRef.current) {
        await loadChats();
        chatListLoadedRef.current = true;
      }
      if (chatId) await loadMessages();
      setLoading(false);
    };
    init();
  }, [chatId, loadMessages, loadChats]);

  // Firestore real-time listener for new messages in the active chat
  useEffect(() => {
    if (!chatId || !firebaseReady) return;

    const q = query(
      collection(firestoreDb, 'messages'),
      where('chat_id', '==', chatId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const msg = change.doc.data();
        if (change.type === 'added' && !loadedIdsRef.current.has(msg.message_id)) {
          loadedIdsRef.current.add(msg.message_id);
          pendingMsgIdRef.current = null;
          setMessages(prev => {
            if (prev.some(m => m.message_id === msg.message_id)) return prev;
            const filtered = msg.sender_id === user?.user_id
              ? prev.filter(m => !(m.pending && m.sender_id === user?.user_id))
              : prev;
            const updated = [...filtered, msg];
            updated.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
            return updated;
          });
          setTotalMessages(prev => prev + 1);
          setChats(prev => {
            const updated = prev.map(c =>
              c.chat_id === chatId
                ? { ...c, last_message: msg.text?.slice(0, 100) || '', last_message_at: msg.created_at }
                : c
            );
            updated.sort((a, b) => (b.last_message_at || '').localeCompare(a.last_message_at || ''));
            return updated;
          });
          if (msg.sender_id !== user?.user_id) {
            chatAPI.markRead(chatId).catch(() => {});
          }
        } else if (change.type === 'modified') {
          const updated = change.doc.data();
          setMessages(prev => prev.map(m => m.message_id === updated.message_id ? updated : m));
        }
      });
    }, (error) => {
      console.warn('[Firestore] Chat listener error:', error);
    });

    return () => unsubscribe();
  }, [chatId, user?.user_id, firebaseReady]);

  // Keep activeChatIdRef in sync + reset unread count when entering a chat
  useEffect(() => {
    activeChatIdRef.current = chatId;
    setActiveChat(chatId || null);
    // Clear draft text and reply state when switching conversations
    setText('');
    setReplyTo(null);
    if (chatId) {
      setChats(prev => prev.map(c => c.chat_id === chatId ? { ...c, unread_count: 0 } : c));
      chatAPI.markRead(chatId).catch(() => {});
    }
    return () => {
      clearTimeout(typingTimerRef.current);
      typingSentRef.current = false;
      if (chatId) chatAPI.setTyping(chatId, false).catch(() => {});
      setIsOtherTyping(false);
      setActiveChat(null);
    };
  }, [chatId, setActiveChat]);

  // Global Firestore listener — tracks new messages across ALL user chats for sidebar unread badges
  useEffect(() => {
    const chatIds = chats.map(c => c.chat_id);
    if (chatIds.length === 0 || !firebaseReady) return;

    const q = query(
      collection(firestoreDb, 'messages'),
      where('chat_id', 'in', chatIds),
      where('created_at', '>=', listenFromRef.current)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type !== 'added') return;
        const msg = change.doc.data();
        if (msg.created_at < listenFromRef.current) return;
        if (msg.chat_id === activeChatIdRef.current) return;

        const isFromOther = msg.sender_id !== user?.user_id;
        setChats(prev => {
          const updated = prev.map(c =>
            c.chat_id === msg.chat_id
              ? {
                  ...c,
                  last_message: msg.text?.slice(0, 100) || '',
                  last_message_at: msg.created_at,
                  unread_count: isFromOther ? (c.unread_count || 0) + 1 : c.unread_count,
                }
              : c
          );
          updated.sort((a, b) => (b.last_message_at || '').localeCompare(a.last_message_at || ''));
          return updated;
        });
      });
    }, (error) => {
      console.warn('[Firestore] Global chats listener error:', error);
    });

    return () => unsubscribe();
  }, [chats.map(c => c.chat_id).join(','), user?.user_id, firebaseReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling fallback when Firestore listeners are unavailable
  useEffect(() => {
    if (firebaseReady || !chatId) return;
    const interval = setInterval(() => {
      loadMessages();
      loadChats();
    }, 4000);
    return () => clearInterval(interval);
  }, [firebaseReady, chatId, loadMessages, loadChats]);

  // Firestore listener for the other user's typing status
  useEffect(() => {
    if (!chatId || !firebaseReady) return;
    const q = query(
      collection(firestoreDb, 'typing_status'),
      where('chat_id', '==', chatId)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const now = Date.now();
      const typing = snapshot.docs
        .map(d => d.data())
        .some(d =>
          d.user_id !== user?.user_id &&
          d.typing === true &&
          now - new Date(d.updated_at).getTime() < 10000
        );
      setIsOtherTyping(typing);
    }, () => { setIsOtherTyping(false); });
    return () => unsubscribe();
  }, [chatId, user?.user_id, firebaseReady]);

  useEffect(() => {
    if (!loading && messages.length > 0 && !initialScrollDone.current) {
      if (messagesContainerRef.current) messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      initialScrollDone.current = true;
    }
  }, [loading, messages.length]);

  // Scroll to bottom on new messages (only if already near bottom)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !initialScrollDone.current) return;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    if (isNearBottom) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isOtherTyping]);

  // Scroll up to load older messages
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el || loadingOlder || !hasOlder) return;
    if (el.scrollTop < 60) {
      loadOlderMessages();
    }
  }, [loadingOlder, hasOlder, loadOlderMessages]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleMessagesScroll);
    return () => el.removeEventListener('scroll', handleMessagesScroll);
  }, [handleMessagesScroll]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || !chatId || sending || mentionOpen) return;
    clearTimeout(typingTimerRef.current);
    typingSentRef.current = false;
    chatAPI.setTyping(chatId, false).catch(() => {});
    const trimmed = text.trim();
    const currentReplyTo = replyTo;
    setSending(true);
    setReplyTo(null);

    const tempId = `temp-${Date.now()}`;
    pendingMsgIdRef.current = tempId;
    const tempMsg = {
      message_id: tempId,
      chat_id: chatId,
      sender_id: user?.user_id,
      sender_name: user?.name || user?.email,
      text: trimmed,
      created_at: new Date().toISOString(),
      pending: true,
      ...(currentReplyTo ? { reply_to: currentReplyTo } : {}),
    };
    setMessages(prev => [...prev, tempMsg]);
    setText('');

    try {
      const res = await chatAPI.sendMessage(chatId, trimmed, currentReplyTo?.message_id || null);
      const realMsg = res.data;
      loadedIdsRef.current.add(realMsg.message_id);
      pendingMsgIdRef.current = null;
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.message_id !== tempId);
        if (withoutTemp.some(m => m.message_id === realMsg.message_id)) return withoutTemp;
        const updated = [...withoutTemp, realMsg];
        updated.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
        return updated;
      });
    } catch {
      setMessages(prev => prev.filter(m => m.message_id !== tempId));
      pendingMsgIdRef.current = null;
      setText(trimmed);
      setReplyTo(currentReplyTo);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleTextChange = (e) => {
    onInputChange(e); // setText + mention detection (via hook)
    // Typing indicator — ChatPage-specific
    if (!chatId) return;
    const val = e.target.value;
    clearTimeout(typingTimerRef.current);
    if (val.trim()) {
      if (!typingSentRef.current) {
        typingSentRef.current = true;
        chatAPI.setTyping(chatId, true).catch(() => {});
      }
      typingTimerRef.current = setTimeout(() => {
        typingSentRef.current = false;
        chatAPI.setTyping(chatId, false).catch(() => {});
      }, 3000);
    } else {
      typingSentRef.current = false;
      chatAPI.setTyping(chatId, false).catch(() => {});
    }
  };

  const activeChat = chats.find(c => c.chat_id === chatId);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    const resetScroll = () => { if (window.scrollY !== 0) window.scrollTo(0, 0); };
    window.addEventListener('scroll', resetScroll, { passive: true });
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.removeEventListener('scroll', resetScroll);
    };
  }, []);

  const isAdmin = ['root', 'org_admin', 'administrator'].includes(user?.permission_level);
  const memberMap = useMemo(() => {
    const map = {};
    messages.forEach(m => {
      if (m.sender_id && m.sender_name && !map[m.sender_id]) {
        map[m.sender_id] = { name: m.sender_name, picture: null };
      }
    });
    chats.forEach(c => {
      if (c.other_user?.user_id) {
        const id = c.other_user.user_id;
        if (map[id]) map[id].picture = c.other_user.picture || null;
        else map[id] = { name: c.other_user.name || c.other_user.email || 'Unknown', picture: c.other_user.picture || null };
      }
    });
    if (user?.user_id) {
      if (map[user.user_id]) map[user.user_id].picture = user.picture || null;
      else map[user.user_id] = { name: user.name || user.email || 'You', picture: user.picture || null };
    }
    return map;
  }, [messages, chats, user?.user_id, user?.picture, user?.name, user?.email]);

  const handleDelete = async (msgId) => {
    setMessages(prev => prev.map(m =>
      m.message_id === msgId ? { ...m, deleted: true } : m
    ));
    try {
      await chatAPI.deleteMessage(chatId, msgId);
    } catch {
      toast.error('Failed to delete message');
      setMessages(prev => prev.map(m =>
        m.message_id === msgId ? { ...m, deleted: false } : m
      ));
    }
  };

  const handleReact = async (msgId, emoji) => {
    setPickerMsgId(null);
    setMessages(prev => prev.map(m => {
      if (m.message_id !== msgId) return m;
      const reactions = { ...(m.reactions || {}) };
      const users = [...(reactions[emoji] || [])];
      const idx = users.indexOf(user?.user_id);
      if (idx >= 0) users.splice(idx, 1);
      else users.push(user?.user_id);
      reactions[emoji] = users;
      return { ...m, reactions };
    }));
    try {
      await chatAPI.react(chatId, msgId, emoji);
    } catch {
      toast.error('Failed to save reaction');
    }
  };

  const handleReply = useCallback((snapshot) => {
    setReplyTo(snapshot);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  /* ---- Long-press (mobile bottom drawer) ---- */
  const startLongPress = useCallback((msg, e) => {
    const touch = e.touches?.[0];
    longPressStartPosRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
    longPressTimerRef.current = setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate(50);
      setLongPressMsg({ ...msg, _touchY: touch?.clientY ?? null });
      setPickerMsgId(null);
    }, 500);
  }, []);

  const cancelLongPress = useCallback((e) => {
    if (e?.type === 'touchmove' && longPressStartPosRef.current) {
      const touch = e.touches?.[0];
      if (touch) {
        const dx = Math.abs(touch.clientX - longPressStartPosRef.current.x);
        const dy = Math.abs(touch.clientY - longPressStartPosRef.current.y);
        if (dx < 8 && dy < 8) return;
      }
    }
    clearTimeout(longPressTimerRef.current);
  }, []);

  const filteredChats = search
    ? chats.filter(c =>
        c.other_user?.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.other_user?.email?.toLowerCase().includes(search.toLowerCase())
      )
    : chats;

  const formatTime = (ts) => {
    try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); }
    catch { return ''; }
  };

  if (loading) {
    return (
    <div className="bg-slate-50">
      <Navbar />
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
      </div>
    </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col bg-slate-50" style={{ paddingBottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))' }}>
      <Navbar />
      <div className="flex-1 flex min-h-0 max-w-7xl mx-auto w-full">
        {/* Chat list sidebar */}
        <div className={`${chatId ? 'hidden md:flex md:w-80 border-r border-slate-200' : 'flex w-full'} flex-col bg-white flex-shrink-0`}>
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="chat-search"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              />
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto" data-testid="chat-list">
            {filteredChats.length === 0 ? (
              <div className="py-12 text-center">
                <MessageSquare className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No conversations yet</p>
                <p className="text-xs text-slate-400 mt-1">Start a chat from the People page</p>
              </div>
            ) : (
              filteredChats.map(c => (
                <button
                  key={c.chat_id}
                  onClick={() => navigate(`/chat/${c.chat_id}`)}
                  data-testid={`chat-item-${c.chat_id}`}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors border-b border-slate-100 ${
                    c.chat_id === chatId ? 'bg-green-50' : ''
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {(c.other_user?.name || c.other_user?.email || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-slate-900 truncate">{c.other_user?.name || c.other_user?.email}</p>
                      {c.unread_count > 0 && (
                        <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-green-600 text-white text-[10px] font-bold px-1.5">
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <p className="text-xs text-slate-500 truncate flex-1">{c.last_message || 'No messages yet'}</p>
                      {c.last_message_at && (
                        <span className="text-[10px] text-slate-400 ml-2 flex-shrink-0">{formatTime(c.last_message_at)}</span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Messages area — only shown when a conversation is selected */}
        <div className={`${chatId ? 'flex' : 'hidden'} flex-col flex-1 min-h-0 overflow-hidden bg-white`}>
          {!chatId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-400">Select a conversation to start messaging</p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="px-4 py-3 bg-white border-b border-slate-200 flex items-center gap-3">
                <button
                  onClick={() => navigate('/chat')}
                  className="md:hidden p-1 text-slate-500 hover:text-slate-700"
                  data-testid="back-to-chats"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-9 h-9 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-semibold">
                  {(activeChat?.other_user?.name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{activeChat?.other_user?.name || 'Unknown'}</p>
                  <p className="text-xs text-slate-500">{activeChat?.other_user?.email}</p>
                </div>
              </div>

              {/* Messages */}
              <div
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-4 space-y-3"
                ref={messagesContainerRef}
                data-testid="messages-area"
                onClick={() => setPickerMsgId(null)}
              >
                {loadingOlder && (
                  <div className="py-2 text-center">
                    <div className="animate-spin inline-block w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full" />
                  </div>
                )}
                {!hasOlder && messages.length > 0 && totalMessages > PAGE_SIZE && (
                  <p className="py-2 text-center text-[10px] text-slate-400">Beginning of conversation</p>
                )}
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm text-slate-400">No messages yet. Say hello!</p>
                  </div>
                )}
                {messages.map(msg => {
                  const isMe = msg.sender_id === user?.user_id;
                  const canDelete = !msg.deleted && (isMe || isAdmin);
                  const resolvedSenderName = isMe
                    ? (user?.name || user?.email || 'You')
                    : (activeChat?.other_user?.name || 'Unknown');
                  return (
                    <ChatMessageBubble
                      key={msg.message_id}
                      msg={msg}
                      isMe={isMe}
                      canDelete={canDelete}
                      userId={user?.user_id}
                      pickerMsgId={pickerMsgId}
                      onSetPickerMsgId={setPickerMsgId}
                      onReply={handleReply}
                      onReact={handleReact}
                      onDelete={handleDelete}
                      onLongPressStart={startLongPress}
                      onLongPressCancel={cancelLongPress}
                      resolvedSenderName={resolvedSenderName}
                      memberMap={memberMap}
                      bubbleContent={
                        <>
                          <p className="text-sm whitespace-pre-wrap break-words">
                            <MessageText text={msg.text} participants={chatParticipants} />
                          </p>
                          <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : ''}`}>
                            <span className={`text-[10px] flex-shrink-0 ${isMe ? 'text-green-200' : 'text-slate-400 dark:text-slate-500'}`}>
                              {msg.pending
                                ? <span className="flex items-center gap-0.5"><span className="w-2 h-2 border border-current rounded-full border-t-transparent animate-spin inline-block" /> Sending…</span>
                                : formatTime(msg.created_at)
                              }
                            </span>
                            {isMe && !msg.pending && (
                              <span className={`text-[10px] flex-shrink-0 ${msg.read ? 'text-green-200' : 'text-green-300/50'}`}>
                                {msg.read ? '\u2713\u2713' : '\u2713'}
                              </span>
                            )}
                          </div>
                        </>
                      }
                    />
                  );
                })}
                {isOtherTyping && (
                  <div className="flex justify-start" data-testid="typing-indicator">
                    <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2">
                      <span className="text-xs text-slate-400">{activeChat?.other_user?.name || 'Someone'} is typing</span>
                      <span className="flex items-center gap-0.5">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '160ms' }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '320ms' }} />
                      </span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Mobile long-press bottom drawer */}
              {longPressMsg && (
                <ChatActionDrawer
                  msg={longPressMsg}
                  userId={user?.user_id}
                  touchY={longPressMsg._touchY}
                  senderDisplayName={
                    longPressMsg.sender_id === user?.user_id
                      ? (user?.name || 'You')
                      : (activeChat?.other_user?.name || 'Unknown')
                  }
                  onReact={handleReact}
                  onReply={(snapshot) => {
                    setReplyTo(snapshot);
                    setTimeout(() => inputRef.current?.focus(), 100);
                  }}
                  onDelete={handleDelete}
                  onClose={() => setLongPressMsg(null)}
                />
              )}

              {/* Input area */}
              <div className="relative border-t border-slate-200 bg-white">
                {/* Mention dropdown — floats above the input */}
                {mentionOpen && filteredParticipants.length > 0 && (
                  <div
                    ref={mentionListRef}
                    data-testid="mention-dropdown"
                    className="absolute bottom-full left-4 right-4 mb-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden z-10"
                  >
                    <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                      <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Mention someone</span>
                      <span className="text-[10px] text-slate-400">↑↓ navigate · Enter select · Esc close</span>
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {filteredParticipants.map((p, i) => (
                        <button
                          key={p.member_id}
                          onMouseDown={(e) => { e.preventDefault(); selectMention(p.name); }}
                          data-testid={`mention-option-${p.member_id}`}
                          className={`w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors ${
                            i === mentionIdx ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300' : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'
                          }`}
                        >
                          <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                            {p.name[0].toUpperCase()}
                          </div>
                          <span className="text-sm font-medium">{p.name}</span>
                          <span className="text-xs text-green-600 dark:text-green-400 font-medium ml-auto">@{p.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Reply preview bar */}
                {replyTo && (
                  <div className="px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800 flex items-center gap-2">
                    <CornerUpLeft className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold text-green-700 dark:text-green-400 truncate">
                        Replying to {replyTo.sender_name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{replyTo.text}</p>
                    </div>
                    <button
                      onClick={() => setReplyTo(null)}
                      data-testid="cancel-reply-btn"
                      className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <form onSubmit={handleSend} className="px-4 py-3 flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={text}
                    onChange={handleTextChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleInputBlur}
                    placeholder="Type a message… use @ to mention"
                    data-testid="message-input"
                    className="flex-1 px-4 py-2.5 text-sm border border-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  />
                  <button
                    type="submit"
                    disabled={!text.trim() || sending}
                    data-testid="send-message-btn"
                    className="p-2.5 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
